import { useCallback, useEffect, useRef, useState } from 'react'
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'
import { db } from 'config/firebase'
import { useAuth } from 'contexts/AuthContext'
import type { AssistantEffort } from 'components/assistant/useAssistantChat'
import {
  normalizeChartTypes,
  normalizeToolPrefs,
  readDebugMode,
  readToolPrefs,
  writeDebugMode,
  writeToolPrefs,
  type AssistantToolPrefs,
} from 'components/assistant/assistantToolPrefs'

const EFFORT_STORAGE_KEY = 'otw.assistant.effort'
const CLOSE_AFTER_NAV_KEY = 'otw.assistant.closeAfterNavigate'
const COLLECTION = 'userAssistantSettings'

export type AssistantPortalSettings = {
  effort: AssistantEffort
  closeAfterNavigate: boolean
  learnFromChats: boolean
  /** Picker choice. Empty means the server default. The API checks it against the approved list. */
  model: string
  toolPrefs: AssistantToolPrefs
}

const DEFAULT_SETTINGS: AssistantPortalSettings = {
  effort: 'high',
  closeAfterNavigate: true,
  learnFromChats: true,
  model: '',
  toolPrefs: readToolPrefs(),
}

function readLocalEffort(): AssistantEffort {
  try {
    const raw = sessionStorage.getItem(EFFORT_STORAGE_KEY)
    if (raw === 'low' || raw === 'medium' || raw === 'high') return raw
  } catch {
    // ignore
  }
  return DEFAULT_SETTINGS.effort
}

function readLocalCloseAfterNavigate(): boolean {
  try {
    const raw = sessionStorage.getItem(CLOSE_AFTER_NAV_KEY)
    if (raw === '0') return false
    if (raw === '1') return true
  } catch {
    // ignore
  }
  return DEFAULT_SETTINGS.closeAfterNavigate
}

function readLocalSettings(): AssistantPortalSettings {
  return {
    effort: readLocalEffort(),
    closeAfterNavigate: readLocalCloseAfterNavigate(),
    learnFromChats: true,
    model: '',
    toolPrefs: readToolPrefs(),
  }
}

function mirrorToSession(settings: AssistantPortalSettings): void {
  try {
    sessionStorage.setItem(EFFORT_STORAGE_KEY, settings.effort)
    sessionStorage.setItem(CLOSE_AFTER_NAV_KEY, settings.closeAfterNavigate ? '1' : '0')
  } catch {
    // ignore
  }
  writeToolPrefs(settings.toolPrefs)
}

function parseRemote(
  data: Record<string, unknown> | undefined,
  local: AssistantPortalSettings,
): AssistantPortalSettings | null {
  if (!data) return null
  const effort =
    data.effort === 'low' || data.effort === 'medium' || data.effort === 'high'
      ? data.effort
      : DEFAULT_SETTINGS.effort
  return {
    effort,
    closeAfterNavigate:
      typeof data.closeAfterNavigate === 'boolean'
        ? data.closeAfterNavigate
        : DEFAULT_SETTINGS.closeAfterNavigate,
    learnFromChats: data.learnFromChats === false ? false : true,
    model: typeof data.model === 'string' ? data.model.slice(0, 64) : '',
    toolPrefs: normalizeToolPrefs({
      ...local.toolPrefs,
      graphing: Boolean(data.graphing),
      chartTypes: normalizeChartTypes(data.chartTypes),
    }),
  }
}

/** Loads/saves portal assistant settings from Firestore (owner-only doc). */
export function useAssistantSettings() {
  const { currentUser } = useAuth()
  const uid = currentUser?.uid ?? null
  const [settings, setSettings] = useState<AssistantPortalSettings>(() => readLocalSettings())
  const [debugMode, setDebugModeState] = useState(() => readDebugMode())
  const [ready, setReady] = useState(false)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const settingsRef = useRef(settings)
  settingsRef.current = settings

  useEffect(() => {
    let cancelled = false
    if (!uid) {
      setSettings(readLocalSettings())
      setReady(true)
      return
    }

    setReady(false)
    ;(async () => {
      try {
        const snap = await getDoc(doc(db, COLLECTION, uid))
        if (cancelled) return
        if (snap.exists()) {
          const remote = parseRemote(snap.data() as Record<string, unknown>, readLocalSettings())
          if (remote) {
            setSettings(remote)
            mirrorToSession(remote)
            setReady(true)
            return
          }
        }
        // First visit: seed Firestore from local/session defaults.
        const local = readLocalSettings()
        setSettings(local)
        mirrorToSession(local)
        await setDoc(
          doc(db, COLLECTION, uid),
          {
            effort: local.effort,
            closeAfterNavigate: local.closeAfterNavigate,
            learnFromChats: local.learnFromChats,
            graphing: local.toolPrefs.graphing,
            chartTypes: local.toolPrefs.chartTypes,
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        )
      } catch (error) {
        console.warn('assistant settings load failed; using local', error)
        if (!cancelled) setSettings(readLocalSettings())
      } finally {
        if (!cancelled) setReady(true)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [uid])

  const persist = useCallback(
    (next: AssistantPortalSettings) => {
      setSettings(next)
      mirrorToSession(next)
      if (!uid) return
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
      saveTimerRef.current = setTimeout(() => {
        void setDoc(
          doc(db, COLLECTION, uid),
          {
            effort: next.effort,
            closeAfterNavigate: next.closeAfterNavigate,
            learnFromChats: next.learnFromChats,
            model: next.model,
            graphing: next.toolPrefs.graphing,
            chartTypes: normalizeChartTypes(next.toolPrefs.chartTypes),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        ).catch(error => {
          console.warn('assistant settings save failed', error)
        })
      }, 250)
    },
    [uid],
  )

  useEffect(
    () => () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    },
    [],
  )

  const setEffort = useCallback(
    (effort: AssistantEffort) => {
      persist({ ...settingsRef.current, effort })
    },
    [persist],
  )

  const setCloseAfterNavigate = useCallback(
    (closeAfterNavigate: boolean) => {
      persist({ ...settingsRef.current, closeAfterNavigate })
    },
    [persist],
  )

  const setLearnFromChats = useCallback(
    (learnFromChats: boolean) => {
      persist({ ...settingsRef.current, learnFromChats })
    },
    [persist],
  )

  const setModel = useCallback(
    (model: string) => {
      persist({ ...settingsRef.current, model: model.slice(0, 64) })
    },
    [persist],
  )

  const setToolPrefs = useCallback(
    (toolPrefs: AssistantToolPrefs) => {
      persist({
        ...settingsRef.current,
        toolPrefs: normalizeToolPrefs(toolPrefs),
      })
    },
    [persist],
  )

  const setDebugMode = useCallback((enabled: boolean) => {
    writeDebugMode(enabled)
    setDebugModeState(enabled)
  }, [])

  return {
    settings,
    ready,
    effort: settings.effort,
    closeAfterNavigate: settings.closeAfterNavigate,
    learnFromChats: settings.learnFromChats,
    model: settings.model,
    toolPrefs: settings.toolPrefs,
    debugMode,
    setEffort,
    setCloseAfterNavigate,
    setLearnFromChats,
    setModel,
    setToolPrefs,
    setDebugMode,
  }
}
